/**
 * SDK 日期栏(B5):日期 pill + 可选动作按钮 + 日历浮层
 *
 * pill 点击切换日历浮层(absolute 在 Shadow DOM 内,不 Portal);
 * 日历确认 -> onDateChange;提供 actionLabel + onAction 时渲染动作按钮(预订/查询),否则纯日期栏。
 * 视觉镜像 hotel-hub DateBar(rounded-full pill、入住/离店/晚数/周几)。
 *
 * 浮层开合动画(对齐 /hub/packages 页面 Sheet):渲染态 rendering = open || closing,
 *   open=true 同帧挂载 + data-state=open(滑入);open=false 时移动端置 closing=true(滑出),
 *   动画结束后卸载;桌面端 closing 不触发 -> 瞬时卸载(与原行为一致,零桌面影响)。
 */
import { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { Calendar } from './Calendar';
import {
  formatMD,
  formatYMD,
  getWeekdayZh,
  differenceInCalendarDays,
  addMonths,
} from '../lib/date';
import { useMediaQuery } from '@/ui/hooks/useMediaQuery';

interface DateBarProps {
  checkIn: Date;
  checkOut: Date;
  onDateChange: (checkIn: Date, checkOut: Date) => void;
  /** 动作按钮文案;与 onAction 同时提供时渲染按钮,缺省即纯日期栏 */
  actionLabel?: string;
  /** 动作按钮回调 */
  onAction?: () => void;
}

const CLOSE_ANIM_MS = 220; // 与 styles.h5.ts 滑出动画时长一致

export function DateBar({ checkIn, checkOut, onDateChange, actionLabel, onAction }: DateBarProps) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false); // 移动端退出动画中
  const [anchor, setAnchor] = useState<'right' | 'left'>('right');
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  // 断点 640px,与 styles.h5.ts 一致。仅驱动"CSS 做不了"的开合动画分支。
  const isMobile = useMediaQuery('(max-width: 640px)', false);

  const nights = differenceInCalendarDays(checkIn, checkOut);
  // 日期格式:PC 显示年月日(formatYMD)、H5 显示月日(formatMD,紧凑)
  const formatDate = isMobile ? formatMD : formatYMD;

  // 打开:取消任何进行中的关闭动画,确保进入态。
  const openCalendar = () => {
    window.clearTimeout(closeTimer.current);
    setClosing(false);
    setOpen(true);
  };

  // 关闭:桌面瞬时卸载;移动端置 closing 播滑出动画,定时结束后由 rendering(=open||closing)卸载。
  // 全部 setState 在事件回调中(非 effect body),避免 cascading render。
  const close = () => {
    if (!open) return;
    window.clearTimeout(closeTimer.current);
    if (!isMobile) {
      setClosing(false);
      setOpen(false);
      return;
    }
    setClosing(true);
    setOpen(false);
    closeTimer.current = window.setTimeout(() => setClosing(false), CLOSE_ANIM_MS);
  };

  const handleRangeChange = (range: { start: Date; end: Date }) => {
    onDateChange(range.start, range.end);
    close();
  };

  // 卸载时清定时器,避免遗留回调
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  // PC 浮层方向自适应:pill 在 header 右侧(package-list)时向左展开,在左侧
  // (date-bar/book-bar)时向右展开,确保不溢出屏幕。H5(≤640)用底部弹层,不参与翻转。
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    const wrap = wrapRef.current;
    const popover = popoverRef.current;
    if (!wrap || !popover) return;
    const wrapRect = wrap.getBoundingClientRect();
    const popW = popover.offsetWidth;
    const vw = window.innerWidth;
    // 右锚(向左展开)不溢出左屏 / 左锚(向右展开)不溢出右屏
    const fitsRight = wrapRect.right - popW >= 8;
    const fitsLeft = wrapRect.left + popW <= vw - 8;
    setAnchor(fitsRight ? 'right' : fitsLeft ? 'left' : 'right');
  }, [open, isMobile]);

  // 点击外部关闭日历浮层:桌面无遮罩(仅 H5 有 backdrop),需手动检测外部点击。
  // Shadow DOM 下 document 收到的事件 target 会被重定向到宿主元素,故用 composedPath()
  // 取真实事件路径,判断点击是否落在浮层/按钮(wrapRef)内;落在内部(pill/日历/backdrop)
  // 不处理,由各自的 onClick 负责切换/选期/关闭,避免与 pill 切换相互冲突。
  // 用 pointerdown(早于 click):pill 打开浮层的那次点击发生在监听挂载前,天然不误关。
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const wrap = wrapRef.current;
      if (wrap && !e.composedPath().includes(wrap)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // 渲染态:open 同帧挂载(保 PC 锚点 effect 行为);closing 期间维持挂载播退出动画。
  const rendering = open || closing;
  // data-state:open 时恒为 open(避免重开瞬闪);仅关闭中为 closed。
  const calState = open ? 'open' : closing ? 'closed' : 'open';

  return (
    <div className="hh-datebar">
      <div className="hh-cal-wrap" ref={wrapRef}>
        <button type="button" className="hh-pill" onClick={() => (open ? close() : openCalendar())}>
          <span className="hh-pill__date">{formatDate(checkIn)}</span>
          <span className="hh-pill__weekday">{getWeekdayZh(checkIn)}入住</span>
          <span className="hh-pill__date" style={{ marginLeft: 8 }}>
            {formatDate(checkOut)}
          </span>
          <span className="hh-pill__weekday">{getWeekdayZh(checkOut)}离店</span>
          <span className="hh-pill__tail">
            <span className="hh-pill__nights">共{nights}晚</span>
            <svg
              className="hh-pill__chevron"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </span>
        </button>

        {rendering && (
          <>
            {/* H5 底部弹层遮罩(桌面 display:none,见 styles);点击关闭 */}
            <div className="hh-cal-backdrop" data-state={calState} onClick={() => close()} />
            <div
              className={`hh-cal-popover${anchor === 'left' ? 'hh-cal-popover--left' : ''}`}
              data-state={calState}
              ref={popoverRef}
            >
              <div className="hh-cal-handle" aria-hidden="true" />
              {/* 关闭按钮(桌面 display:none;移动右上角,对齐页面 Sheet) */}
              <button
                type="button"
                className="hh-cal-close"
                aria-label="关闭"
                onClick={() => close()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              <Calendar
                defaultStartDate={checkIn}
                defaultEndDate={checkOut}
                minDate={new Date()}
                maxDate={addMonths(new Date(), 6)}
                onChange={handleRangeChange}
              />
            </div>
          </>
        )}
      </div>

      {actionLabel && onAction && (
        <button type="button" className="hh-action" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}
